import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  BuildingOffice2Icon,
  HomeIcon,
  MapIcon,
  TableCellsIcon,
  Squares2X2Icon,
  MagnifyingGlassIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';

import { Card, Badge, Spinner, Button } from '@realvisor/ui';
import { useProperties, usePropertiesMap, PROPERTY_TYPE_LABELS, type PropertyMapItem } from '@/hooks';
import { useProject } from '@/contexts/ProjectContext';

const MAPY_API_KEY = import.meta.env['VITE_MAPY_API_KEY'] || '';

const getMapStyle = () => {
  const tiles = MAPY_API_KEY
    ? [`https://api.mapy.cz/v1/maptiles/basic/256/{z}/{x}/{y}?apikey=${MAPY_API_KEY}`]
    : ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'];
  
  const attribution = MAPY_API_KEY
    ? '<a href="https://api.mapy.cz/copyright" target="_blank">© Seznam.cz a.s. a další</a>'
    : '© OpenStreetMap contributors';

  return {
    version: 8,
    glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
    sources: {
      basemap: {
        type: 'raster',
        tiles: tiles,
        tileSize: 256,
        attribution: attribution,
        maxzoom: 19,
      },
    },
    layers: [
      {
        id: 'basemap',
        type: 'raster',
        source: 'basemap',
      },
    ],
  };
};

function formatPrice(value: number | null | undefined): string {
  if (!value) return '-';
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M Kč`;
  if (value >= 1000) return `${Math.round(value / 1000)}K Kč`;
  return `${value.toLocaleString()} Kč`;
}

function formatArea(value: number | null | undefined): string {
  if (!value) return '-';
  return `${value} m²`;
}

const PROPERTY_TYPE_ICONS: Record<string, typeof BuildingOffice2Icon> = {
  flat: BuildingOffice2Icon,
  house: HomeIcon,
  land: MapPinIcon,
};

const PROPERTY_TYPE_COLORS: Record<string, string> = {
  flat: '#3B82F6',
  house: '#10B981',
  land: '#F59E0B',
};

interface MapComponentProps {
  properties: PropertyMapItem[];
}

function MapComponent({ properties }: MapComponentProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const popupRef = useRef<any>(null);

  useEffect(() => {
    if (!mapRef.current || properties.length === 0) return;

    const loadMapLibre = async () => {
      if (!document.getElementById('maplibre-css')) {
        const link = document.createElement('link');
        link.id = 'maplibre-css';
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/maplibre-gl@4.1.2/dist/maplibre-gl.css';
        document.head.appendChild(link);
      }

      if (!(window as any).maplibregl) {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/maplibre-gl@4.1.2/dist/maplibre-gl.js';
        script.async = true;
        script.onload = () => initMap();
        document.head.appendChild(script);
      } else {
        initMap();
      }
    };

    const initMap = () => {
      if (!mapRef.current || mapInstanceRef.current) return;

      const container = mapRef.current;
      if (container.clientWidth === 0 || container.clientHeight === 0) {
        setTimeout(initMap, 100);
        return;
      }

      const maplibregl = (window as any).maplibregl;

      const firstProperty = properties[0];
      const defaultCenter: [number, number] = firstProperty 
        ? [firstProperty.lng, firstProperty.lat] 
        : [14.4378, 50.0755];

      const map = new maplibregl.Map({
        container: container,
        style: getMapStyle(),
        center: defaultCenter,
        zoom: 7,
      });

      map.addControl(new maplibregl.NavigationControl(), 'top-right');
      map.addControl(new maplibregl.ScaleControl(), 'bottom-left');

      mapInstanceRef.current = map;

      map.on('load', () => {
        const geojson = {
          type: 'FeatureCollection',
          features: properties.map((p, idx) => ({
            type: 'Feature',
            id: idx,
            geometry: {
              type: 'Point',
              coordinates: [p.lng, p.lat],
            },
            properties: {
              id: p.id,
              address: p.address,
              city: p.city || '',
              type: p.type,
              localType: p.localType || '',
              avgPrice: p.avgPrice || 0,
              avgPriceM2: p.avgPriceM2 || 0,
              leadId: p.leadId || '',
              color: p.project?.color || PROPERTY_TYPE_COLORS[p.type] || '#3B82F6',
            },
          })),
        };

        map.addSource('properties', {
          type: 'geojson',
          data: geojson,
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 50,
        });

        map.addLayer({
          id: 'clusters',
          type: 'circle',
          source: 'properties',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': [
              'step',
              ['get', 'point_count'],
              '#3B82F6',
              10, '#8B5CF6',
              30, '#EC4899',
            ],
            'circle-radius': [
              'step',
              ['get', 'point_count'],
              20,
              10, 25,
              30, 30,
            ],
            'circle-stroke-width': 3,
            'circle-stroke-color': '#fff',
          },
        });

        map.addLayer({
          id: 'cluster-count',
          type: 'symbol',
          source: 'properties',
          filter: ['has', 'point_count'],
          layout: {
            'text-field': '{point_count_abbreviated}',
            'text-font': ['Open Sans Semibold'],
            'text-size': 14,
          },
          paint: {
            'text-color': '#ffffff',
          },
        });

        map.addLayer({
          id: 'unclustered-point',
          type: 'circle',
          source: 'properties',
          filter: ['!', ['has', 'point_count']],
          paint: {
            'circle-color': ['get', 'color'],
            'circle-radius': 12,
            'circle-stroke-width': 3,
            'circle-stroke-color': '#fff',
          },
        });

        map.on('click', 'clusters', (e: any) => {
          const features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
          const clusterId = features[0].properties.cluster_id;
          const pointCount = features[0].properties.point_count;
          const coordinates = features[0].geometry.coordinates.slice();
          const source = map.getSource('properties');

          source.getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
            if (err) return;

            if (zoom > 18 || map.getZoom() >= 17) {
              source.getClusterLeaves(clusterId, pointCount, 0, (err2: any, leaves: any[]) => {
                if (err2 || !leaves) return;

                if (popupRef.current) {
                  popupRef.current.remove();
                }

                const itemsHtml = leaves.map((leaf: any) => {
                  const p = leaf.properties;
                  return `
                    <div style="padding: 8px 0; border-bottom: 1px solid #eee;">
                      <p style="margin: 0; font-weight: 600; font-size: 13px; color: #111;">${p.address}</p>
                      <p style="margin: 2px 0; font-size: 11px; color: #666;">
                        ${p.city}${p.localType ? ' • ' + p.localType : ''}
                      </p>
                      ${p.avgPrice > 0 ? `<p style="margin: 2px 0; font-weight: 600; font-size: 13px; color: #10B981;">${formatPrice(p.avgPrice)}</p>` : ''}
                      ${p.leadId ? `<a href="/leads/${p.leadId}" style="color: #3B82F6; font-size: 11px; text-decoration: none;">Zobrazit lead →</a>` : ''}
                    </div>
                  `;
                }).join('');

                popupRef.current = new maplibregl.Popup({ offset: 15, maxWidth: '320px' })
                  .setLngLat(coordinates)
                  .setHTML(`
                    <div style="padding: 8px;">
                      <h3 style="margin: 0 0 8px; font-weight: 700; font-size: 14px; color: #111; border-bottom: 2px solid #3B82F6; padding-bottom: 8px;">
                        ${pointCount} nemovitostí na této adrese
                      </h3>
                      <div style="max-height: 300px; overflow-y: auto;">
                        ${itemsHtml}
                      </div>
                    </div>
                  `)
                  .addTo(map);
              });
            } else {
              map.easeTo({
                center: coordinates,
                zoom: zoom,
              });
            }
          });
        });

        map.on('click', 'unclustered-point', (e: any) => {
          const feature = e.features[0];
          const coordinates = feature.geometry.coordinates.slice();
          const props = feature.properties;

          while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
          }

          if (popupRef.current) {
            popupRef.current.remove();
          }

          const priceHtml = props.avgPrice > 0 ? `
            <p style="margin: 8px 0 4px; font-weight: 700; font-size: 16px; color: #10B981;">
              ${formatPrice(props.avgPrice)}
            </p>
            ${props.avgPriceM2 > 0 ? `<p style="margin: 0; font-size: 11px; color: #888;">${props.avgPriceM2.toLocaleString()} Kč/m²</p>` : ''}
          ` : '';

          const leadHtml = props.leadId ? `
            <a href="/leads/${props.leadId}" 
               style="display: inline-block; margin-top: 8px; padding: 4px 12px; background: #3B82F6; color: white; border-radius: 4px; text-decoration: none; font-size: 12px; font-weight: 500;">
              Zobrazit lead →
            </a>
          ` : '';

          const panoramaHtml = props.panoramaUrl ? `
            <img src="${props.panoramaUrl}" alt="Street View" 
                 style="width: 100%; height: 100px; object-fit: cover; border-radius: 6px; margin-bottom: 8px;" />
          ` : '';

          popupRef.current = new maplibregl.Popup({ offset: 15, maxWidth: '280px' })
            .setLngLat(coordinates)
            .setHTML(`
              <div style="padding: 8px;">
                ${panoramaHtml}
                <h3 style="margin: 0 0 8px; font-weight: 600; font-size: 14px; color: #111;">${props.address}</h3>
                <p style="margin: 0 0 4px; font-size: 12px; color: #666;">
                  ${props.city}${props.localType ? ' • ' + props.localType : ''}
                </p>
                ${priceHtml}
                ${leadHtml}
              </div>
            `)
            .addTo(map);
        });

        map.on('mouseenter', 'clusters', () => {
          map.getCanvas().style.cursor = 'pointer';
        });
        map.on('mouseleave', 'clusters', () => {
          map.getCanvas().style.cursor = '';
        });
        map.on('mouseenter', 'unclustered-point', () => {
          map.getCanvas().style.cursor = 'pointer';
        });
        map.on('mouseleave', 'unclustered-point', () => {
          map.getCanvas().style.cursor = '';
        });

        if (properties.length > 1) {
          const bounds = new maplibregl.LngLatBounds();
          properties.forEach((p) => bounds.extend([p.lng, p.lat]));
          map.fitBounds(bounds, { padding: 50, maxZoom: 14 });
        } else if (properties.length === 1 && firstProperty) {
          map.flyTo({ center: [firstProperty.lng, firstProperty.lat], zoom: 15 });
        }
      });
    };

    loadMapLibre();

    return () => {
      if (popupRef.current) {
        popupRef.current.remove();
      }
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [properties]);

  return (
    <div 
      ref={mapRef} 
      className="w-full h-full rounded-lg"
      style={{ minHeight: '600px' }}
    />
  );
}

const VIEW_MODE_KEY = 'properties-view-mode';

export default function Properties() {
  const [viewMode, setViewMode] = useState<'list' | 'cards' | 'map'>(() => {
    const saved = localStorage.getItem(VIEW_MODE_KEY);
    if (saved === 'list' || saved === 'cards' || saved === 'map') {
      return saved;
    }
    return 'list';
  });
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [page, setPage] = useState(1);

  const handleViewModeChange = (mode: 'list' | 'cards' | 'map') => {
    setViewMode(mode);
    localStorage.setItem(VIEW_MODE_KEY, mode);
  };
  
  const { currentProject, isAllProjects } = useProject();
  const projectId = isAllProjects ? undefined : currentProject?.id;

  const { data: properties, meta, isLoading } = useProperties({
    projectId,
    type: typeFilter || undefined,
    search: search || undefined,
    page,
    limit: viewMode === 'cards' ? 12 : 20,
  });

  const { data: mapData, isLoading: mapLoading } = usePropertiesMap(projectId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Nemovitosti</h1>
          <p className="text-sm text-gray-500">
            {meta?.total || 0} nemovitostí s oceněním
          </p>
        </div>
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => handleViewModeChange('list')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'list'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <TableCellsIcon className="h-4 w-4" />
            Seznam
          </button>
          <button
            onClick={() => handleViewModeChange('cards')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'cards'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Squares2X2Icon className="h-4 w-4" />
            Kartičky
          </button>
          <button
            onClick={() => handleViewModeChange('map')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'map'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <MapIcon className="h-4 w-4" />
            Mapa
          </button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <div className="p-4 flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Hledat podle adresy..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="">Všechny typy</option>
            <option value="flat">Byty</option>
            <option value="house">Domy</option>
            <option value="land">Pozemky</option>
          </select>
        </div>
      </Card>

      {/* List View */}
      {viewMode === 'list' && (
        <Card>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : properties && properties.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Nemovitost</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Typ</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Plocha</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Odhad ceny</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Cena/m²</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Projekt</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Lead</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {properties.map((property) => {
                    const Icon = PROPERTY_TYPE_ICONS[property.type] || BuildingOffice2Icon;
                    return (
                      <tr key={property.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100">
                              <Icon className="h-5 w-5 text-primary-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{property.address}</p>
                              <p className="text-sm text-gray-500">{property.city || property.district || '-'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="default">{PROPERTY_TYPE_LABELS[property.type] || property.type}</Badge>
                          {property.localType && <span className="ml-2 text-sm text-gray-500">{property.localType}</span>}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {property.type === 'land' ? formatArea(property.lotArea) : formatArea(property.floorArea)}
                        </td>
                        <td className="px-4 py-3">
                          {property.latestValuation?.avgPrice ? (
                            <span className="font-semibold text-green-600">{formatPrice(property.latestValuation.avgPrice)}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {property.latestValuation?.avgPriceM2 ? `${property.latestValuation.avgPriceM2.toLocaleString()} Kč` : '-'}
                        </td>
                        <td className="px-4 py-3">
                          {property.project ? (
                            <span
                              className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium"
                              style={{ backgroundColor: `${property.project.color || '#3B82F6'}20`, color: property.project.color || '#3B82F6' }}
                            >
                              {property.project.name}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {property.latestValuation?.leadId ? (
                            <Link to={`/leads/${property.latestValuation.leadId}`} className="text-primary-600 hover:underline text-sm">
                              Zobrazit
                            </Link>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-12 text-center text-gray-500">Žádné nemovitosti nenalezeny</div>
          )}

          {meta && meta.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
              <p className="text-sm text-gray-500">Strana {meta.page} z {meta.totalPages}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>Předchozí</Button>
                <Button variant="outline" size="sm" disabled={page === meta.totalPages} onClick={() => setPage(page + 1)}>Další</Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Cards View */}
      {viewMode === 'cards' && (
        <>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : properties && properties.length > 0 ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {properties.map((property) => {
                const Icon = PROPERTY_TYPE_ICONS[property.type] || BuildingOffice2Icon;
                const color = property.project?.color || PROPERTY_TYPE_COLORS[property.type] || '#3B82F6';
                return (
                  <Card key={property.id} className="hover:shadow-md transition-shadow overflow-hidden">
                    {/* Street View Panorama */}
                    {property.panoramaUrl ? (
                      <div className="h-32 bg-gray-100">
                        <img 
                          src={property.panoramaUrl} 
                          alt="Street View" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div 
                        className="h-32 flex items-center justify-center"
                        style={{ backgroundColor: `${color}10` }}
                      >
                        <Icon className="h-12 w-12" style={{ color, opacity: 0.5 }} />
                      </div>
                    )}
                    <div className="p-4">
                      <div className="flex items-start gap-3 mb-3">
                        <div
                          className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg"
                          style={{ backgroundColor: `${color}15` }}
                        >
                          <Icon className="h-5 w-5" style={{ color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 truncate">{property.address}</p>
                          <p className="text-sm text-gray-500 truncate">{property.city || property.district}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="default">{PROPERTY_TYPE_LABELS[property.type]}</Badge>
                        {property.localType && (
                          <span className="text-xs text-gray-500">{property.localType}</span>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                        <div>
                          <p className="text-gray-500">Plocha</p>
                          <p className="font-medium">
                            {property.type === 'land' ? formatArea(property.lotArea) : formatArea(property.floorArea)}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Cena/m²</p>
                          <p className="font-medium">
                            {property.latestValuation?.avgPriceM2
                              ? `${property.latestValuation.avgPriceM2.toLocaleString()} Kč`
                              : '-'}
                          </p>
                        </div>
                      </div>

                      {property.latestValuation?.avgPrice && (
                        <div className="bg-green-50 rounded-lg p-3 mb-3">
                          <p className="text-xs text-green-600 mb-1">Odhad ceny</p>
                          <p className="text-lg font-bold text-green-700">
                            {formatPrice(property.latestValuation.avgPrice)}
                          </p>
                        </div>
                      )}

                      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                        {property.project ? (
                          <span
                            className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                            style={{ backgroundColor: `${property.project.color || '#3B82F6'}15`, color: property.project.color || '#3B82F6' }}
                          >
                            {property.project.name}
                          </span>
                        ) : (
                          <span />
                        )}
                        {property.latestValuation?.leadId && (
                          <Link
                            to={`/leads/${property.latestValuation.leadId}`}
                            className="text-sm text-primary-600 hover:underline font-medium"
                          >
                            Zobrazit lead →
                          </Link>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card>
              <div className="py-12 text-center text-gray-500">Žádné nemovitosti nenalezeny</div>
            </Card>
          )}

          {meta && meta.totalPages > 1 && (
            <div className="flex items-center justify-center gap-4">
              <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>Předchozí</Button>
              <span className="text-sm text-gray-500">Strana {meta.page} z {meta.totalPages}</span>
              <Button variant="outline" size="sm" disabled={page === meta.totalPages} onClick={() => setPage(page + 1)}>Další</Button>
            </div>
          )}
        </>
      )}

      {/* Map View */}
      {viewMode === 'map' && (
        <Card>
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-medium text-gray-500">
                {mapData?.length || 0} nemovitostí na mapě
              </p>
            </div>
            {mapLoading ? (
              <div className="flex items-center justify-center py-12" style={{ minHeight: '600px' }}>
                <Spinner size="lg" />
              </div>
            ) : mapData && mapData.length > 0 ? (
              <MapComponent
                properties={mapData}
              />
            ) : (
              <div className="flex items-center justify-center py-12 text-gray-500" style={{ minHeight: '600px' }}>
                <div className="text-center">
                  <MapIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p>Žádné nemovitosti k zobrazení na mapě</p>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
